import { Badge } from '@/components/ui/badge'
import { getCompletenessVariant, getCompletenessLabel } from '@/utils/dataQuality'
import { Info } from 'lucide-react'

interface CompletenessIndicatorProps {
  /**
   * The completeness percentage (0-100)
   */
  percentage: number

  /**
   * Whether to show the label with the percentage
   * @default true
   */
  showLabel?: boolean

  /**
   * Whether to show the descriptive quality label (Excellent, Good, etc.)
   * @default false
   */
  showQualityLabel?: boolean

  /**
   * Additional CSS classes
   */
  className?: string

  /**
   * Whether to show an info icon with tooltip
   * @default false
   */
  showInfo?: boolean
}

export function CompletenessIndicator({
  percentage,
  showLabel = true,
  showQualityLabel = false,
  className = '',
  showInfo = false,
}: CompletenessIndicatorProps) {
  const variant = getCompletenessVariant(percentage)
  const qualityLabel = getCompletenessLabel(percentage)

  // Determine aria-label for accessibility
  const ariaLabel = `Data completeness: ${percentage}% (${qualityLabel})`

  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      <Badge variant={variant} className="font-medium" aria-label={ariaLabel} role="status">
        {showLabel && `${percentage}% Complete`}
        {!showLabel && `${percentage}%`}
      </Badge>

      {showQualityLabel && (
        <span className="text-xs text-muted-foreground" aria-hidden="true">
          ({qualityLabel})
        </span>
      )}

      {showInfo && (
        <span title="Completeness is calculated based on filled required and important fields">
          <Info
            className="h-4 w-4 text-muted-foreground cursor-help"
            aria-label="Completeness is calculated based on filled required and important fields"
          />
        </span>
      )}
    </div>
  )
}
